import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { InstantSearch, SearchBox, Index, Configure, useHits, useInstantSearch } from 'react-instantsearch';
import {
  Circuitry,
  MagnifyingGlass,
  ShoppingCart,
  CaretDown,
  List,
  MapPin,
  Wine,
  Clock,
  Storefront,
  ArrowRight,
  Phone,
  Info,
  Star,
  Globe
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { searchClient } from '../../services/algoliaClient';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { RecentSearchesManager } from '../../utils/recentSearchesManager';
import { categories } from '../../data/categories';
import { insights } from '../../services/algoliaInsights';
import { recordEvent } from '../../utils/userProfile';

import { RecentSearches } from '../Search/RecentSearches';
import { QuerySuggestionsFromIndex } from '../Search/QuerySuggestions';
import { HitsSection } from '../Search/HitsSection';
import { Megamenu } from '../Navigation/Megamenu';
import { CartDropdown } from '../Cart/CartDropdown';
import Logo from '../../logo.svg'; // Use the logo placeholder for now

// Conditional Query Suggestions Component
const ConditionalQuerySuggestions = ({ query, onSuggestionSelect }) => {
  const { items } = useHits();
  
  // Only render if there are actual suggestions
  if (items.length === 0) {
    return null;
  }
  
  return <QuerySuggestionsFromIndex onSuggestionSelect={onSuggestionSelect} />;
};

// Mobile Products Section Component
const MobileProductsSection = ({ query, onProductClick, onViewAll }) => {
  const { items } = useHits();
  const { results } = useInstantSearch();
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div style={{ 
        color: '#888', 
        fontSize: 15, 
        textAlign: 'center', 
        padding: '32px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px'
      }}>
        <Wine size={48} color="#4B164C" />
        <h4 style={{ margin: 0, color: '#4B164C', fontSize: '18px' }}>{t('no_results')}</h4>
        <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>{t('discover_wines')}</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#4B164C'
        }}>
          <Wine size={18} />
          <span>{t('products')}</span>
        </div>
        <button
          onClick={onViewAll}
          style={{
            background: 'none',
            border: 'none',
            color: '#4B164C',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8f6fa'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {t('view_all')}
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Products Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {items.slice(0, 6).map((hit) => (
          <div
            key={hit.objectID}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              borderRadius: '12px',
              padding: '12px',
              border: '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minHeight: '140px'
            }}
            onMouseOver={e => {
              e.currentTarget.style.borderColor = '#4B164C';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(75,22,76,0.1)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.borderColor = '#f0f0f0';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => onProductClick && onProductClick(hit, results?.queryID)}
          >
            <img
              src={hit.image}
              alt={hit.name}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'contain',
                marginBottom: '8px',
                borderRadius: '8px',
                background: '#faf9f7'
              }}
            />
            <div style={{
              fontWeight: '600',
              fontSize: '13px',
              color: '#4B164C',
              textAlign: 'center',
              lineHeight: '1.2',
              marginBottom: '4px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {hit.name}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#888',
              fontWeight: '500'
            }}>
              {hit.price ? `${hit.price} €` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Featured Products Component for empty search
const FeaturedProducts = ({ onProductClick }) => {
  const { t } = useTranslation();
  
  return (
    <Index indexName="saq_products">
      <Configure 
        hitsPerPage={6} 
        analyticsTags={['auto-complete']}
        ruleContexts={['auto-complete']}
      />
      <EmptyStateContent onProductClick={onProductClick} />
    </Index>
  );
};

// Banner Component for empty search
const Banner = ({ banner }) => {
  if (!banner || !banner.image || !banner.image.urls || banner.image.urls.length === 0) {
    return null;
  }

  const imageUrl = banner.image.urls[0].url;
  const title = banner.image.title || '';
  const linkUrl = banner.link?.url || '';

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#4B164C'
        }}>
          <Star size={18} weight="fill" />
          <span>Promotion</span>
        </div>
      </div>

      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        overflow: 'hidden',
        cursor: linkUrl ? 'pointer' : 'default',
        transition: 'all 0.2s ease'
      }}
      onMouseOver={e => {
        if (linkUrl) {
          e.currentTarget.style.borderColor = '#4B164C';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(75,22,76,0.1)';
        }
      }}
      onMouseOut={e => {
        if (linkUrl) {
          e.currentTarget.style.borderColor = '#f0f0f0';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (linkUrl) {
          window.open(linkUrl, '_blank');
        }
      }}
      >
        <img
          src={imageUrl}
          alt={title}
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'cover'
          }}
        />
        {title && (
          <div style={{
            padding: '12px',
            fontSize: '14px',
            color: '#4B164C',
            fontWeight: '500',
            lineHeight: '1.3'
          }}>
            {title}
          </div>
        )}
      </div>
    </div>
  );
};

// Empty State Component that shows banner or products
const EmptyStateContent = ({ onProductClick }) => {
  const { hits } = useHits();
  const { results } = useInstantSearch();
  
  // Debug: Log the results to see the structure
  console.log('EmptyStateContent - results:', results);
  console.log('EmptyStateContent - renderingContent:', results?.renderingContent);
  
  // Check if we have banner data in the renderingContent
  const hasBanner = results && 
    results.renderingContent && 
    results.renderingContent.widgets && 
    results.renderingContent.widgets.banners && 
    results.renderingContent.widgets.banners.length > 0;
  
  console.log('EmptyStateContent - hasBanner:', hasBanner);
  if (hasBanner) {
    console.log('EmptyStateContent - banner data:', results.renderingContent.widgets.banners[0]);
  }
  
  if (hasBanner) {
    return <Banner banner={results.renderingContent.widgets.banners[0]} />;
  }
  
  // Fallback to products if no banner
  return (
    <div style={{ width: '100%' }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#4B164C'
        }}>
          <Star size={18} weight="fill" />
          <span>Produits populaires</span>
        </div>
      </div>

      {/* Products Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {hits.slice(0, 6).map((hit) => (
          <div
            key={hit.objectID}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: '#fff',
              borderRadius: '12px',
              padding: '12px',
              border: '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minHeight: '140px'
            }}
            onMouseOver={e => {
              e.currentTarget.style.borderColor = '#4B164C';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(75,22,76,0.1)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.borderColor = '#f0f0f0';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onClick={() => onProductClick && onProductClick(hit, results?.queryID)}
          >
            <img
              src={hit.image}
              alt={hit.name}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'contain',
                marginBottom: '8px',
                borderRadius: '8px',
                background: '#faf9f7'
              }}
            />
            <div style={{
              fontWeight: '600',
              fontSize: '13px',
              color: '#4B164C',
              textAlign: 'center',
              lineHeight: '1.2',
              marginBottom: '4px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {hit.name}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#888',
              fontWeight: '500'
            }}>
              {hit.price ? `${hit.price} €` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Add a useMediaQuery hook for responsive detection
function useMediaQuery(query) {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  return matches;
}

// Helper function to get current day of week in French
const getCurrentDay = () => {
  const now = new Date();
  const currentDay = now.getDay();
  const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return dayNames[currentDay];
};

// Helper function to get store hours for current day
const getStoreHours = (store) => {
  if (!store || !store.opening_hours) return null;
  
  const currentDay = getCurrentDay();
  const dayHours = store.opening_hours.find(hour => 
    hour.day.toLowerCase() === currentDay
  );
  
  if (!dayHours) return null;
  
  return dayHours.hours;
};

// Helper function to check if store is currently open
const isStoreOpen = (store) => {
  if (!store || !store.opening_hours || store.opening_hours.length === 0) {
    return false;
  }
  
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const currentTime = now.getHours() * 60 + now.getMinutes(); // Convert to minutes
  
  // Map JavaScript day numbers to French day names
  const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const todayName = dayNames[currentDay];
  
  const todayHours = store.opening_hours.find(day => day.day.toLowerCase() === todayName);
  if (!todayHours || !todayHours.hours) return false;
  
  // Parse opening hours (format: "9h00-18h00" or "9:00-18:00" or "9h00-00h00")
  const hoursMatch = todayHours.hours.match(/(\d{1,2})[h:](\d{2})-(\d{1,2})[h:](\d{2})/);
  if (!hoursMatch) return false;
  
  const openHour = parseInt(hoursMatch[1]);
  const openMinute = parseInt(hoursMatch[2]);
  const closeHour = parseInt(hoursMatch[3]);
  const closeMinute = parseInt(hoursMatch[4]);
  
  const openTime = openHour * 60 + openMinute;
  let closeTime = closeHour * 60 + closeMinute;
  
  // Handle stores open past midnight (e.g., 22h00-02h00)
  if (closeTime < openTime) {
    closeTime += 24 * 60; // Add 24 hours
  }
  
  return currentTime >= openTime && currentTime <= closeTime;
};

// Header with enhanced search functionality
export const Header = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [megamenuOpen, setMegamenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [selectedStoreForDetails, setSelectedStoreForDetails] = useState(null);
  const debounceTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { getCartItemsCount, setIsCartOpen } = useCart();
  const {
    selectedStore,
    setSelectedStore,
    selectedStoreData,
    persona,
    setPersona,
    storeResults = [],
    searchStores,
    storeQuery,
    loadingStores
  } = useStore();

  const isMobile = useMediaQuery('(max-width: 767px)');
  
  // Get store hours and open status from selectedStoreData
  const storeHours = getStoreHours(selectedStoreData);
  const isOpen = isStoreOpen(selectedStoreData);

  // Load all stores when the locator opens (empty Algolia query = browse index)
  useEffect(() => {
    if (storeModalOpen) {
      searchStores('');
    }
  }, [storeModalOpen, searchStores]);

  useEffect(() => {
    setDropdownOpen((query !== '' && query.length >= 1) || isFocused);
  }, [query, isFocused]);

  const handleEscKey = (event) => {
    if (event.key === 'Escape') {
      setDropdownOpen(false);
      setQuery('');
      setMegamenuOpen(false);
      setMobileMenuOpen(false);
      setUserMenuOpen(false);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  const queryHook = useCallback((query, search) => {
    setQuery(query);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Only trigger search if:
    // 1. The query is not empty
    // 2. The dropdown is open (focused)
    if (query.trim() && isFocused) {
      debounceTimeoutRef.current = setTimeout(() => {
        search(query);
      }, 200);
    }
  }, [isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setDropdownOpen(true);
  }
  
  const handleBlur = (event) => {
    // Use a small delay to check if the click was inside the dropdown
    setTimeout(() => {
      const dropdownElement = document.querySelector('[data-dropdown="search-dropdown"]');
      const activeElement = document.activeElement;
      
      // Don't close dropdown if the active element is inside the dropdown
      if (dropdownElement && activeElement && dropdownElement.contains(activeElement)) {
        return;
      }
      
      // Don't close dropdown if the click target is inside the dropdown
      if (dropdownElement && event && event.relatedTarget && dropdownElement.contains(event.relatedTarget)) {
        return;
      }
      
      setIsFocused(false);
      if (query === '' || query.length < 1) {
        setDropdownOpen(false);
      }
    }, 100);
  }

  const performSearch = (searchQuery) => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      RecentSearchesManager.addSearch(trimmedQuery);
      navigate(`/search?query=${encodeURIComponent(trimmedQuery)}`);
      setDropdownOpen(false);
      setQuery('');
      
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
        searchInputRef.current.blur();
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    performSearch(query);
  };

  const handleSearchSelect = (selectedQuery) => {
    performSearch(selectedQuery);
  };

  const handleRecentSearchesClear = () => {
    // Force re-render of recent searches
    setQuery('');
  };

  const handleLogoClick = () => {
    navigate('/');
    setQuery('');
    setDropdownOpen(false);
    setIsFocused(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleCartClick = () => {
    setIsCartOpen(true);
  };

  const handleUserMenuToggle = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleStoreChange = (storeId) => {
    // Ensure we always store the store ID consistently
    const storeToSet = typeof storeId === 'object' ? storeId.store_id : storeId;
    const storeData = typeof storeId === 'object' ? storeId : null;
    
    setSelectedStore(storeToSet, storeData);
    setStoreModalOpen(false);
  };

  const handlePersonaChange = (newPersona) => {
    setPersona(newPersona);
    setUserMenuOpen(false);
  };

  const handleProductClick = (hit, queryID = null) => {
    setDropdownOpen(false);
    setQuery('');
    
    // Track click event for search dropdown products - use queryID when from search for attribution
    if (queryID) {
      insights.clickedObjectIDsAfterSearch(
        'Search Dropdown Product Click',
        'saq_products',
        [hit.objectID],
        [hit.__position ?? 0],
        queryID
      );
    } else {
      insights.clickedObjectIDs(
        'Search Dropdown Product Click',
        'saq_products',
        [hit.objectID]
      );
    }

    recordEvent({
      type: 'click',
      product: { ...hit, selectedStore: String(selectedStore || '1') },
    });

    navigate(`/produit/${hit.objectID}`);
  };
  
  useEffect(() => {
    const inputElement = document.querySelector('.ais-SearchBox-input');
    if (inputElement) {
      searchInputRef.current = inputElement;
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    const formElement = document.querySelector('.ais-SearchBox-form');
    if (formElement) {
      formElement.addEventListener('submit', handleSubmit);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
      if (formElement) {
        formElement.removeEventListener('submit', handleSubmit);
      }
    };
  });

  return (
    <header style={{
      width: '100%',
      background: '#fff',
      boxShadow: '0 2px 8px rgba(75,22,76,0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      {isMobile ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: 56 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleLogoClick}>
            <span style={{ fontSize: 28, marginRight: 6 }}>🍷</span>
            <span style={{ color: '#4B164C', fontWeight: 'bold', fontSize: 18 }}>PasLaSAQ</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 4, color: '#4B164C' }} onClick={handleCartClick}>
              <ShoppingCart size={24} />
              {getCartItemsCount() > 0 && (
                <span style={{ position: 'absolute', top: -2, right: -2, background: '#4B164C', color: '#fff', borderRadius: '50%', minWidth: 18, height: 18, fontSize: 11, padding: '0 4px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getCartItemsCount()}</span>
              )}
            </button>
            {/* Hamburger menu */}
            <button style={{ background: 'none', border: 'none', fontSize: 28, color: '#4B164C', cursor: 'pointer' }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <List size={28} />
          </button>
          </div>
          {/* Mobile slide-out menu */}
          {mobileMenuOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 200 }} onClick={() => setMobileMenuOpen(false)}>
              <div style={{ background: '#fff', width: '80vw', maxWidth: 320, height: '100%', padding: 24, boxShadow: '2px 0 16px rgba(75,22,76,0.12)', position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 24, color: '#4B164C', cursor: 'pointer' }} onClick={() => setMobileMenuOpen(false)}>×</button>
                
                {/* Categories Section */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#4B164C', fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
                    {t('categories')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {categories.map(category => {
                      const IconComponent = category.icon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => {
                            navigate(`/categories/${category.slug}`);
                            setMobileMenuOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            background: 'none',
                            border: '1px solid #e0e0e0',
                            borderRadius: 12,
                            padding: '12px 16px',
                            color: '#4B164C',
                            fontSize: 16,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textAlign: 'left',
                            width: '100%'
                          }}
                          onMouseOver={e => {
                            e.currentTarget.style.borderColor = '#4B164C';
                            e.currentTarget.style.background = '#f8f6fa';
                          }}
                          onMouseOut={e => {
                            e.currentTarget.style.borderColor = '#e0e0e0';
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <IconComponent size={20} color="#4B164C" />
                          <span>{category.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Info Section */}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#4B164C', fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
                    Guide du Vin
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                      onClick={() => {
                        navigate('/info');
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        background: 'none',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        padding: '12px 16px',
                        color: '#4B164C',
                        fontSize: 16,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        width: '100%'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.borderColor = '#4B164C';
                        e.currentTarget.style.background = '#f8f6fa';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Info size={20} color="#4B164C" />
                      <span>Guide du Vin</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/info/countries');
                        setMobileMenuOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        background: 'none',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        padding: '12px 16px',
                        color: '#4B164C',
                        fontSize: 16,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        width: '100%'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.borderColor = '#4B164C';
                        e.currentTarget.style.background = '#f8f6fa';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Globe size={20} color="#4B164C" />
                      <span>Pays Viticoles</span>
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ color: '#4B164C', fontWeight: 500, marginBottom: 8 }}>{t('store')}</div>
                  <div 
                    onClick={() => { setStoreModalOpen(true); setMobileMenuOpen(false); }} 
                    style={{ 
                      cursor: 'pointer', 
                      color: '#4B164C', 
                      fontWeight: 500, 
                      padding: '8px 16px', 
                      borderRadius: 12, 
                      background: selectedStoreData ? '#f8f6fa' : '#fff3e0', 
                      border: `2px solid ${selectedStoreData ? '#4B164C' : '#f57c00'}`, 
                      minWidth: 120, 
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'left',
                      boxShadow: '0 2px 8px rgba(75,22,76,0.08)'
                    }}
                  >
                    {selectedStoreData ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: isOpen ? '#e8f5e8' : '#ffebee',
                          color: isOpen ? '#388e3c' : '#d32f2f'
                        }}>
                          <Storefront size={16} weight="bold" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: 13, 
                            marginBottom: 2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {selectedStoreData.name}
                          </div>
                          {storeHours && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 4, 
                              fontSize: 11, 
                              color: isOpen ? '#388e3c' : '#d32f2f',
                              fontWeight: 500
                            }}>
                              <Clock size={10} weight="bold" />
                              <span>{storeHours}</span>
                              <span style={{ 
                                background: isOpen ? '#388e3c' : '#d32f2f',
                                color: '#fff',
                                padding: '1px 4px',
                                borderRadius: 6,
                                fontSize: 9,
                                fontWeight: 600,
                                marginLeft: 4
                              }}>
                                {isOpen ? 'OUVERT' : 'FERMÉ'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6,
                        color: '#f57c00'
                      }}>
                        <MapPin size={18} />
                        <span style={{ fontWeight: 600 }}>{t('select_store')}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 4, color: '#4B164C' }} onClick={handleCartClick}>
                    <ShoppingCart size={22} />
                    {getCartItemsCount() > 0 && (
                      <span style={{ position: 'absolute', top: -6, right: -6, background: '#4B164C', color: '#fff', borderRadius: '50%', fontSize: 12, padding: '2px 6px', fontWeight: 700 }}>{getCartItemsCount()}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Mobile search bar - always visible on mobile */}
          {isMobile && (
            <div style={{ position: 'fixed', top: 56, left: 0, width: '100vw', background: '#fff', zIndex: 150, padding: '12px 16px', borderBottom: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <InstantSearch searchClient={searchClient} indexName="saq_products" insights={{ insightsInitParams: { userToken: insights.userToken } }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#f8f6fa', borderRadius: 8, border: '1px solid #4B164C', padding: '0 8px', position: 'relative' }}>
                  <SearchBox
                    queryHook={queryHook}
                    translations={{ placeholder: t('search_placeholder') }}
                    style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 16, outline: 'none' }}
                  />
                  <button type="submit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4B164C' }} onClick={handleSubmit}>
                    <MagnifyingGlass size={20} weight="bold" />
                  </button>
                </div>
                {dropdownOpen && (
                  <div 
                    data-dropdown="search-dropdown"
                    style={{
                      position: 'absolute',
                      top: isMobile ? 48 : 56,
                      left: 0,
                      minWidth: isMobile ? '100vw' : 700,
                      maxWidth: isMobile ? '100vw' : 900,
                      width: isMobile ? '100vw' : '100%',
                      background: '#fff',
                      borderRadius: isMobile ? 0 : 22,
                      boxShadow: isMobile ? '0 8px 32px rgba(75,22,76,0.10)' : '0 16px 48px rgba(75,22,76,0.10)',
                      zIndex: 200,
                      padding: 0,
                      display: isMobile ? 'block' : 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
                      gap: 0,
                      overflow: 'hidden',
                      maxHeight: isMobile ? '90vh' : '70vh',
                      overflowY: 'auto',
                      border: 'none',
                      right: isMobile ? 0 : 'auto',
                      left: isMobile ? 0 : 0,
                    }}>
                    {/* Recent Searches Section */}
                    <div style={{ 
                      padding: '20px 16px 16px 16px',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <RecentSearches onSearchSelect={handleSearchSelect} onClear={handleRecentSearchesClear} />
                    </div>
                    
                    {/* Query Suggestions Section - Only if there are suggestions */}
                    {query && query.length >= 2 && (
                      <div style={{ 
                        padding: '16px',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <Index indexName="saq_products_query_suggestions">
                          <Configure 
                            query={query} 
                            hitsPerPage={4}
                            analyticsTags={['auto-complete']}
                            ruleContexts={['auto-complete']}
                          />
                          <ConditionalQuerySuggestions query={query} onSuggestionSelect={handleSearchSelect} />
                        </Index>
                      </div>
                    )}
                    
                    {/* Products Section */}
                    <div style={{ padding: '16px' }}>
                      {query && query.length >= 2 ? (
                        <Index indexName="saq_products">
                          <Configure 
                            hitsPerPage={6}
                            analyticsTags={['auto-complete']}
                            ruleContexts={['auto-complete']}
                          />
                          {isMobile ? (
                            <MobileProductsSection 
                              query={query} 
                              onProductClick={handleProductClick}
                              onViewAll={() => {
                                performSearch(query);
                                setDropdownOpen(false);
                              }}
                            />
                          ) : (
                            <HitsSection title="" query={query} minimalDropdown={true} isMobile={isMobile} onProductClick={handleProductClick} />
                          )}
                        </Index>
                      ) : (
                        <FeaturedProducts onProductClick={handleProductClick} />
                      )}
                    </div>
                  </div>
                )}
              </InstantSearch>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', height: 64, gap: 16, flexWrap: 'wrap' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleLogoClick}>
            <span style={{ fontSize: 28, marginRight: 6 }}>🍷</span>
            <span style={{ color: '#4B164C', fontWeight: 'bold', fontSize: 22 }}>PasLaSAQ</span>
          </div>
          {/* Nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button style={{ background: 'none', border: 'none', color: '#4B164C', fontWeight: 500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setMegamenuOpen(!megamenuOpen)}>
              <List size={18} />
              <span>{t('categories')}</span>
            </button>
            <button 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#4B164C', 
                fontWeight: 500, 
                fontSize: 16, 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6,
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'background 0.2s'
              }} 
              onClick={() => navigate('/info')}
              onMouseOver={e => e.currentTarget.style.background = '#f8f6fa'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              <Info size={18} />
              <span>Guide du Vin</span>
            </button>
          </nav>
          {/* Search */}
          <div style={{ flex: 1, minWidth: 320, maxWidth: 600, margin: '0 16px', position: 'relative' }}>
            <InstantSearch searchClient={searchClient} indexName="saq_products" insights={true}>
              <div style={{ display: 'flex', alignItems: 'center', background: '#f8f6fa', borderRadius: 24, border: '1.5px solid #4B164C', padding: '0 16px', position: 'relative', height: 48, boxShadow: '0 2px 8px rgba(75,22,76,0.06)' }}>
                <SearchBox
                  queryHook={queryHook}
                  translations={{ placeholder: t('search_placeholder') }}
                  style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 18, outline: 'none', height: 44, padding: '0 8px' }}
                />
                <button type="submit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4B164C', marginLeft: 8, height: 44, display: 'flex', alignItems: 'center' }} onClick={handleSubmit}>
                  <MagnifyingGlass size={24} weight="bold" />
                </button>
              </div>
              {dropdownOpen && (
                <div 
                  data-dropdown="search-dropdown"
                  style={{
                    position: 'absolute',
                    top: isMobile ? 48 : 56,
                    left: 0,
                    minWidth: isMobile ? '100vw' : 700,
                    maxWidth: isMobile ? '100vw' : 900,
                    width: isMobile ? '100vw' : '100%',
                    background: '#fff',
                    borderRadius: isMobile ? 0 : 22,
                    boxShadow: isMobile ? '0 8px 32px rgba(75,22,76,0.10)' : '0 16px 48px rgba(75,22,76,0.10)',
                    zIndex: 200,
                    padding: 0,
                    display: isMobile ? 'block' : 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
                    gap: 0,
                    overflow: 'hidden',
                    maxHeight: isMobile ? '90vh' : '70vh',
                    overflowY: 'auto',
                    border: 'none',
                    right: isMobile ? 0 : 'auto',
                    left: isMobile ? 0 : 0,
                  }}>
                  {/* Left column: Recent searches & suggestions */}
                  <div style={{ borderRight: isMobile ? 'none' : '1px solid #f0f0f0', padding: isMobile ? '24px 16px 16px 16px' : '32px 32px 32px 40px', minWidth: 220, background: '#fff' }}>
                    <div style={{ marginBottom: 24 }}>
                      <RecentSearches onSearchSelect={handleSearchSelect} onClear={handleRecentSearchesClear} />
                    </div>
                    {query && query.length >= 2 && (
                      <div>
                        <Index indexName="saq_products_query_suggestions">
                          <Configure 
                            query={query} 
                            hitsPerPage={4}
                            analyticsTags={['auto-complete']}
                            ruleContexts={['auto-complete']}
                          />
                          <ConditionalQuerySuggestions query={query} onSuggestionSelect={handleSearchSelect} />
                        </Index>
                      </div>
                    )}
                  </div>
                  {/* Right column: Products */}
                  <div style={{ padding: isMobile ? '16px 8px' : '32px 40px', minWidth: isMobile ? '100%' : 340, background: '#fff' }}>
                    {query && query.length >= 2 ? (
                      <Index indexName="saq_products">
                        <Configure 
                          hitsPerPage={isMobile ? 6 : 4}
                          analyticsTags={['auto-complete']}
                          ruleContexts={['auto-complete']}
                        />
                        {isMobile ? (
                          <MobileProductsSection 
                            query={query} 
                            onProductClick={handleProductClick}
                            onViewAll={() => {
                              performSearch(query);
                              setDropdownOpen(false);
                            }}
                          />
                        ) : (
                          <HitsSection title="" query={query} minimalDropdown={true} isMobile={isMobile} onProductClick={handleProductClick} />
                        )}
                      </Index>
                    ) : (
                      <FeaturedProducts onProductClick={handleProductClick} />
                    )}
                  </div>
                </div>
              )}
            </InstantSearch>
          </div>
          {/* Store selector */}
          <div style={{ marginRight: 16 }}>
            <div 
              className="selected-store-display" 
              onClick={() => setStoreModalOpen(true)} 
              style={{ 
                cursor: 'pointer', 
                color: '#4B164C', 
                fontWeight: 500, 
                padding: '8px 16px', 
                borderRadius: 12, 
                background: selectedStoreData ? '#f8f6fa' : '#fff3e0', 
                border: `2px solid ${selectedStoreData ? '#4B164C' : '#f57c00'}`, 
                minWidth: 220, 
                maxWidth: 280,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(75,22,76,0.08)'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {selectedStoreData ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isOpen ? '#e8f5e8' : '#ffebee',
                    color: isOpen ? '#388e3c' : '#d32f2f'
                  }}>
                    <Storefront size={18} weight="bold" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: 14, 
                      marginBottom: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {selectedStoreData.name}
                    </div>
                    {storeHours && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 4, 
                        fontSize: 12, 
                        color: isOpen ? '#388e3c' : '#d32f2f',
                        fontWeight: 500
                      }}>
                        <Clock size={12} weight="bold" />
                        <span>{storeHours}</span>
                        <span style={{ 
                          background: isOpen ? '#388e3c' : '#d32f2f',
                          color: '#fff',
                          padding: '1px 6px',
                          borderRadius: 8,
                          fontSize: 10,
                          fontWeight: 600,
                          marginLeft: 4
                        }}>
                          {isOpen ? 'OUVERT' : 'FERMÉ'}
                        </span>
                      </div>
                    )}
                  </div>
                  <CaretDown size={16} style={{ color: '#4B164C', flexShrink: 0 }} />
                </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8,
                  color: '#f57c00'
                }}>
                  <MapPin size={20} />
                  <span style={{ fontWeight: 600 }}>{t('select_store')}</span>
                  <CaretDown size={16} />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: 4, color: '#4B164C' }} onClick={handleCartClick}>
              <ShoppingCart size={22} />
              {getCartItemsCount() > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -6, background: '#4B164C', color: '#fff', borderRadius: '50%', fontSize: 12, padding: '2px 6px', fontWeight: 700 }}>{getCartItemsCount()}</span>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Overlay for dropdowns */}
      {(userMenuOpen || mobileMenuOpen) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100,
          }}
          onClick={() => {
            setUserMenuOpen(false);
            setMobileMenuOpen(false);
          }}
        />
      )}

      {storeModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            maxWidth: 500,
            width: '90%',
            height: '600px', // Fixed height
            maxHeight: '80vh',
            margin: '0 auto',
            boxShadow: '0 16px 48px rgba(75,22,76,0.20)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: 24,
              paddingBottom: 16,
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0 // Prevent header from shrinking
            }}>
              <h3 style={{ color: '#4B164C', fontSize: 24, fontWeight: 700, margin: 0 }}>
                <MapPin size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                {t('select_store')}
              </h3>
              <button 
                onClick={() => setStoreModalOpen(false)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: 24, 
                  color: '#888', 
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 8,
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                ×
              </button>
            </div>
            {/* Store search input restored */}
            <div style={{ marginBottom: 16, flexShrink: 0 }}> {/* Prevent search from shrinking */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#f8f6fa',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1px solid #e0e0e0'
              }}>
                <MagnifyingGlass size={20} color="#4B164C" style={{ marginRight: 12 }} />
                <input
                  type="text"
                  placeholder="Rechercher une succursale..."
                  value={storeQuery}
                  onChange={e => searchStores(e.target.value)}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: 16,
                    color: '#4B164C',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            {/* Store results area with fixed height */}
            <div style={{
              flex: 1,
              height: '400px', // Fixed height for results area
              overflowY: 'auto',
              borderRadius: 12,
              border: '1px solid #e0e0e0',
              background: '#faf9fb',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: loadingStores ? 'center' : 'flex-start',
              alignItems: 'center',
              position: 'relative'
            }}>
              {loadingStores ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  color: '#4B164C',
                  fontWeight: 500,
                  fontSize: 18
                }}>
                  <div className="loading-spinner" style={{ marginBottom: 12 }}>
                    <svg width="32" height="32" viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#4B164C" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.415, 31.415" transform="rotate(72.5046 25 25)">
                        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                      </circle>
                    </svg>
                  </div>
                  Recherche de succursales...
                </div>
              ) : storeResults.length > 0 ? (
                storeResults.map(store => {
                  const storeHours = getStoreHours(store);
                  const storeIsOpen = isStoreOpen(store);
                  return (
                    <div key={store.objectID} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      width: '100%', 
                      padding: '16px', 
                      border: 'none', 
                      background: selectedStore === store.store_id ? '#f3e6f7' : 'transparent', 
                      color: '#4B164C', 
                      textAlign: 'left', 
                      borderRadius: 0,
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.2s'
                    }}>
                      <button
                        style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          flex: 1,
                          border: 'none',
                          background: 'transparent',
                          color: '#4B164C',
                          cursor: 'pointer',
                          textAlign: 'left',
                          padding: 0
                        }}
                        onMouseOver={e => e.currentTarget.parentElement.style.background = selectedStore === store.store_id ? '#f3e6f7' : '#f8f6fa'}
                        onMouseOut={e => e.currentTarget.parentElement.style.background = selectedStore === store.store_id ? '#f3e6f7' : 'transparent'}
                        onClick={() => { handleStoreChange(store); }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: storeIsOpen ? '#e8f5e8' : '#ffebee',
                          color: storeIsOpen ? '#388e3c' : '#d32f2f',
                          marginRight: 16,
                          flexShrink: 0
                        }}>
                          <Storefront size={20} weight="bold" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 600, 
                            fontSize: 16, 
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            {store.name}
                            {selectedStore === store.store_id && (
                              <span style={{ 
                                background: '#4B164C',
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 600
                              }}>
                                SÉLECTIONNÉ
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: '#888', marginBottom: 2 }}>
                            {store.address?.street}{store.address?.street ? ', ' : ''}{store.address?.city}{store.address?.city ? ', ' : ''}{store.address?.region}
                          </div>
                          <div style={{ fontSize: 12, color: storeIsOpen ? '#388e3c' : '#d32f2f', fontWeight: 500 }}>{storeIsOpen ? 'Ouvert' : 'Fermé'} {storeHours && `· ${storeHours}`}</div>
                        </div>
                      </button>
                      <button
                        onClick={() => setSelectedStoreForDetails(store)}
                        style={{
                          background: 'none',
                          border: '1px solid #4B164C',
                          color: '#4B164C',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          marginLeft: 12,
                          transition: 'all 0.2s',
                          flexShrink: 0
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = '#4B164C';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#4B164C';
                        }}
                      >
                        Détails
                      </button>
                    </div>
                  );
                })
              ) : (
                <div style={{ color: '#888', fontSize: 16, padding: 32, textAlign: 'center', width: '100%' }}>
                  Aucune succursale trouvée.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Store Details Modal */}
      {selectedStoreForDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200,
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            maxWidth: 600,
            width: '90%',
            maxHeight: '80vh',
            margin: '0 auto',
            boxShadow: '0 16px 48px rgba(75,22,76,0.20)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: 24,
              paddingBottom: 16,
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0
            }}>
              <h3 style={{ color: '#4B164C', fontSize: 24, fontWeight: 700, margin: 0 }}>
                <Storefront size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Détails de la succursale
              </h3>
              <button 
                onClick={() => setSelectedStoreForDetails(null)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: 24, 
                  color: '#888', 
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: 8,
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                ×
              </button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {/* Store Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 24,
                padding: 20,
                background: '#f8f6fa',
                borderRadius: 12
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 60,
                  height: 60,
                  borderRadius: 12,
                  background: selectedStoreForDetails && isStoreOpen(selectedStoreForDetails) ? '#e8f5e8' : '#ffebee',
                  color: selectedStoreForDetails && isStoreOpen(selectedStoreForDetails) ? '#388e3c' : '#d32f2f',
                  marginRight: 20,
                  flexShrink: 0
                }}>
                  <Storefront size={30} weight="bold" />
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ color: '#4B164C', fontSize: 20, fontWeight: 700, margin: '0 0 8px 0' }}>
                    {selectedStoreForDetails?.name}
                  </h2>
                  <div style={{ 
                    fontSize: 14, 
                    color: selectedStoreForDetails && isStoreOpen(selectedStoreForDetails) ? '#388e3c' : '#d32f2f', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: selectedStoreForDetails && isStoreOpen(selectedStoreForDetails) ? '#388e3c' : '#d32f2f'
                    }}></div>
                    {selectedStoreForDetails && isStoreOpen(selectedStoreForDetails) ? 'Ouvert' : 'Fermé'}
                  </div>
                </div>
              </div>

              {/* Store Information */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ color: '#4B164C', fontSize: 16, fontWeight: 600, margin: '0 0 12px 0' }}>
                  <MapPin size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Adresse
                </h4>
                <div style={{ 
                  padding: 16, 
                  background: '#faf9fb', 
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.5
                }}>
                  {selectedStoreForDetails?.address?.street && (
                    <div style={{ marginBottom: 4 }}>{selectedStoreForDetails.address.street}</div>
                  )}
                  {selectedStoreForDetails?.address?.city && (
                    <div style={{ marginBottom: 4 }}>{selectedStoreForDetails.address.city}</div>
                  )}
                  {selectedStoreForDetails?.address?.region && (
                    <div>{selectedStoreForDetails.address.region}</div>
                  )}
                </div>
              </div>

              {/* Opening Hours */}
              {selectedStoreForDetails?.opening_hours && (
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: '#4B164C', fontSize: 16, fontWeight: 600, margin: '0 0 12px 0' }}>
                    <Clock size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Heures d'ouverture
                  </h4>
                  <div style={{ 
                    background: '#faf9fb', 
                    borderRadius: 8,
                    overflow: 'hidden'
                  }}>
                    {selectedStoreForDetails.opening_hours.map((day, index) => {
                      const isToday = day.day.toLowerCase() === getCurrentDay();
                      return (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderBottom: index < selectedStoreForDetails.opening_hours.length - 1 ? '1px solid #e0e0e0' : 'none',
                          background: isToday ? '#f3e6f7' : 'transparent',
                          fontWeight: isToday ? 600 : 400
                        }}>
                          <span style={{ color: '#4B164C' }}>
                            {day.day.charAt(0).toUpperCase() + day.day.slice(1)}
                            {isToday && (
                              <span style={{ 
                                background: '#4B164C',
                                color: '#fff',
                                padding: '2px 6px',
                                borderRadius: 8,
                                fontSize: 10,
                                fontWeight: 600,
                                marginLeft: 8
                              }}>
                                AUJOURD'HUI
                              </span>
                            )}
                          </span>
                          <span style={{ color: '#666', fontWeight: 500 }}>{day.hours || 'Fermé'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              {selectedStoreForDetails?.phone && (
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: '#4B164C', fontSize: 16, fontWeight: 600, margin: '0 0 12px 0' }}>
                    <Phone size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Contact
                  </h4>
                  <div style={{ 
                    padding: 16, 
                    background: '#faf9fb', 
                    borderRadius: 8,
                    fontSize: 14
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Téléphone:</strong> {selectedStoreForDetails.phone}
                    </div>
                    {selectedStoreForDetails.email && (
                      <div>
                        <strong>Email:</strong> {selectedStoreForDetails.email}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {selectedStoreForDetails?.description && (
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: '#4B164C', fontSize: 16, fontWeight: 600, margin: '0 0 12px 0' }}>
                    <Info size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Informations
                  </h4>
                  <div style={{ 
                    padding: 16, 
                    background: '#faf9fb', 
                    borderRadius: 8,
                    fontSize: 14,
                    lineHeight: 1.5
                  }}>
                    {selectedStoreForDetails.description}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0',
              flexShrink: 0
            }}>
              <button
                onClick={() => { 
                  handleStoreChange(selectedStoreForDetails);
                  setSelectedStoreForDetails(null);
                }}
                style={{
                  flex: 1,
                  background: '#4B164C',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#3a1240'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Sélectionner cette succursale
              </button>
              <button
                onClick={() => setSelectedStoreForDetails(null)}
                style={{
                  background: 'transparent',
                  color: '#4B164C',
                  border: '1px solid #4B164C',
                  padding: '12px 24px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <Megamenu isOpen={megamenuOpen} setIsOpen={setMegamenuOpen} />
      <CartDropdown />

      <style>{`
        .ais-SearchBox-input {
          background: transparent !important;
          border: none !important;
          outline: none !important;
          width: 100% !important;
          height: 44px !important;
          font-size: 18px !important;
          box-shadow: none !important;
          padding: 0 8px !important;
        }
        .ais-SearchBox-form {
          box-shadow: none !important;
          border: none !important;
          background: transparent !important;
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
        }
        /* Hide native search/clear buttons */
        .ais-SearchBox-input::-webkit-search-decoration,
        .ais-SearchBox-input::-webkit-search-cancel-button,
        .ais-SearchBox-input::-webkit-search-results-button,
        .ais-SearchBox-input::-webkit-search-results-decoration {
          display: none !important;
        }
        .ais-SearchBox-input[type="search"]::-ms-clear {
          display: none !important;
          width: 0;
          height: 0;
        }
        .ais-SearchBox-input[type="search"]::-ms-reveal {
          display: none !important;
          width: 0;
          height: 0;
        }
        .ais-SearchBox-input[type="search"]::-o-clear {
          display: none !important;
        }
        .ais-SearchBox-input[type="search"]::-moz-search-clear {
          display: none !important;
        }
        .ais-SearchBox-input[type="search"]::-moz-search-results {
          display: none !important;
        }
        /* Hide Algolia default submit/reset icons/buttons */
        .ais-SearchBox-submit,
        .ais-SearchBox-submitIcon,
        .ais-SearchBox-reset,
        .ais-SearchBox-resetIcon {
          display: none !important;
        }
      `}</style>
    </header>
  );
};